import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from 'react-i18next';

const LIST_ADD_REQUEST_DIALOG_COPY = {
  de: {
    title: 'Parameter zu öffentlicher Liste hinzufügen',
    publicListLabel: 'Öffentliche Liste:',
    publicListInfo: 'Ihre Anfrage wird an die Listen-Verwalter gesendet und muss genehmigt werden.',
    listInformation: 'Listen-Information:',
    createdBy: 'Erstellt von: {{value}}',
    currentParameters: 'Aktuelle Parameter: {{count}}',
    confirmPrompt: 'Möchten Sie die Aufnahme dieses Parameters in diese öffentliche Liste vorschlagen?',
    cancel: 'Abbrechen',
    submitSuggestion: 'Vorschlag senden',
  },
  en: {
    title: 'Add parameter to public list',
    publicListLabel: 'Public list:',
    publicListInfo: 'Your request will be sent to the list maintainers and must be approved.',
    listInformation: 'List information:',
    createdBy: 'Created by: {{value}}',
    currentParameters: 'Current parameters: {{count}}',
    confirmPrompt: 'Do you want to suggest adding this parameter to this public list?',
    cancel: 'Cancel',
    submitSuggestion: 'Send suggestion',
  },
  pt: {
    title: 'Adicionar parâmetro à lista pública',
    publicListLabel: 'Lista pública:',
    publicListInfo: 'Sua solicitação será enviada aos responsáveis pela lista e precisará ser aprovada.',
    listInformation: 'Informações da lista:',
    createdBy: 'Criado por: {{value}}',
    currentParameters: 'Parâmetros atuais: {{count}}',
    confirmPrompt: 'Deseja sugerir a inclusão deste parâmetro nesta lista pública?',
    cancel: 'Cancelar',
    submitSuggestion: 'Enviar sugestão',
  },
};

/**
 * List Addition Request Dialog Component
 * Confirmation dialog for adding parameter to public list
 *
 * @param {boolean} open - Whether dialog is open
 * @param {Function} onClose - Handler for closing dialog
 * @param {Object} list - List to add parameter to
 * @param {number} parameterId - Parameter ID to add
 * @param {Function} onConfirm - Handler for confirming addition request
 */
const ListAddRequestDialog = ({
  open,
  onClose,
  list,
  parameterId,
  onConfirm,
}) => {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en'
      : 'de';
  const copy = LIST_ADD_REQUEST_DIALOG_COPY[language];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PublicIcon />
          {copy.title}
        </Box>
      </DialogTitle>
      <DialogContent>
        {list && (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>{copy.publicListLabel}</strong> {copy.publicListInfo}
              </Typography>
            </Alert>

            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {copy.listInformation}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <PublicIcon fontSize="small" color="primary" />
                <Typography variant="body1" fontWeight="bold">
                  {list.name}
                </Typography>
              </Box>
              <Typography variant="caption" color="textSecondary" display="block">
                {copy.createdBy.replace('{{value}}', list.createdBy)}
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block">
                {copy.currentParameters.replace('{{count}}', list.parameterIds.length)}
              </Typography>
            </Paper>

            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
              {copy.confirmPrompt}
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{copy.cancel}</Button>
        <Button
          variant="contained"
          onClick={onConfirm}
          startIcon={<AddIcon />}
        >
          {copy.submitSuggestion}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ListAddRequestDialog;
